import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { StorageAdapter } from "./Storage/StorageAdapter";
import { formatBytes, formatDuration, ALL_ACCEPTED } from "./Utilities/Utility";
import { Icons } from "./Utilities/Icons";
import { MediaCard } from "./Utilities/MediaCard";
import { UploadZone } from "./Utilities/UploadZone";
import { EmptyState } from "./Utilities/EmptyState";
import MediaViewer from "../Viewer/MediaViewer";

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const TABS = [
    { key: "image", label: "Images", icon: Icons.Image },
    { key: "video", label: "Videos", icon: Icons.Video },
    { key: "pdf", label: "PDFs", icon: Icons.Pdf },
    { key: "upload", label: "Upload New", icon: Icons.Upload },
];

export default function Library({ onItemSelected }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("image");
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    const refresh = useCallback(async () => {
        const all = await StorageAdapter.getAll();
        setItems(all);
    }, []);

    useEffect(() => {
        refresh();
        const unsub = StorageAdapter.subscribe(refresh);
        return unsub;
    }, [refresh]);

    const filteredItems = useMemo(() => {
        if (activeTab === "upload") return [];
        return items.filter((i) => i.type === activeTab);
    }, [items, activeTab]);

    // const handleSelect = (id) => {
    //     setSelectedId((prev) => (prev === id ? null : id));

    //     const item = items.find((i) => i.id === id);
    //     if (item) {
    //         navigate("/viewer", {
    //             state: {
    //                 type: item.type,
    //                 src: item.dataUrl,
    //                 name: item.name,
    //             },
    //         });
    //     }
    // }

    const handleSelect = (id) => {
        setSelectedId((prev) => (prev === id ? null : id));
        const item = items.find((i) => i.id === id);
        if (item && onItemSelected) onItemSelected(item);
    };

    const handleDelete = async (id) => {
        if (selectedId === id) setSelectedId(null);
        await StorageAdapter.deleteItem(id);
    };

    const handleFilesAdded = async (files) => {
        const validFiles = files.filter((f) =>
            f.type.startsWith("image/") || f.type.startsWith("video/") || f.type === "application/pdf"
        );
        await Promise.all(validFiles.map((f) => StorageAdapter.addItem(f)));
    };

    const counts = useMemo(() => {
        const c = { image: 0, video: 0, pdf: 0 };
        items.forEach((i) => { if (c[i.type] !== undefined) c[i.type]++; });
        return c;
    }, [items]);

    const selectedItem = items.find((i) => i.id === selectedId);

    return (
        <div
            style={{
                width: "100%",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                background: "#ffffff",
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                color: "#1f2937",
                overflow: "hidden",
            }}
        >
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

            {/* ─── TAB BAR ─── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    padding: "8px 16px 0",
                    borderBottom: "1px solid #e5e7eb",
                    flexShrink: 0,
                    background: "#fff",
                }}
            >
                {TABS.map(({ key, label, icon: Icon }) => {
                    const active = activeTab === key;
                    const count = key !== "upload" ? counts[key] : null;
                    return (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "10px 14px 11px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: active ? 600 : 500,
                                color: active ? "#1f2937" : "#9ca3af",
                                borderBottom: active ? "2px solid #7c3aed" : "2px solid transparent",
                                marginBottom: "-1px",
                                transition: "all 0.15s",
                                fontFamily: "inherit",
                            }}
                        >
                            <Icon />
                            <span>{label}</span>
                            {count !== null && count > 0 && (
                                <span
                                    style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        background: active ? "#ede9fe" : "#f3f4f6",
                                        color: active ? "#7c3aed" : "#9ca3af",
                                        padding: "1px 7px",
                                        borderRadius: "10px",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* ─── CONTENT AREA (flex:1 fills all remaining space) ─── */}
            <div style={{ flex: "1 1 0%", overflow: "auto", background: "#fff" }}>
                {activeTab === "upload" ? (
                    <UploadZone onFilesAdded={handleFilesAdded} />
                ) : filteredItems.length === 0 ? (
                    <EmptyState type={activeTab} />
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                            gap: "10px",
                            padding: "14px 16px",
                            alignContent: "start",
                        }}
                    >
                        {filteredItems.map((item) => (
                            <MediaCard
                                key={item.id}
                                item={item}
                                isSelected={selectedId === item.id}
                                onSelect={handleSelect}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ─── BOTTOM BAR (selected file info) ─── */}
            {selectedItem && (
                <div
                    style={{
                        flexShrink: 0,
                        padding: "9px 16px",
                        borderTop: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#fafafa",
                        fontSize: "12px",
                    }}
                >
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#7c3aed", flexShrink: 0 }} />
                    <span style={{ color: "#374151", fontWeight: 500 }}>{selectedItem.name}</span>
                    <span style={{ color: "#9ca3af" }}>{formatBytes(selectedItem.size)}</span>
                </div>
            )}
        </div>
    );
}